// src/pages/IndiceChiffreAffaires.jsx
import React, { useState, useEffect, useMemo } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import { useNavigate } from 'react-router-dom';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Container,
  Typography,
  Grid,
  Card,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  styled,
  createTheme,
  ThemeProvider,
  Box,
  CardMedia,
  Button,
} from '@mui/material';
import moment from 'moment';
import Footer from '../navbar/Footer';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { getPublications } from '../services/publicationService';

function IndiceChiffreAffaires() {
  const navigate = useNavigate();
  const [visibleCount, setVisibleCount] = useState(4);
  const [selectedYear, setSelectedYear] = useState('Tous');
  const [icData, setIcData] = useState([]);
  const [filteredIC, setFilteredIC] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Récupération des données depuis l'API
  useEffect(() => {
    setLoading(true);
    getPublications({ type: 'ica', perPage: 1000 })
      .then((res) => {
        if (res.error) {
          setError(res.error);
        } else {
          // Mapper les données de l'API vers la structure attendue
          const mapped = res.data.map((item) => ({
            title: item.title,
            link: item.document_url,
            image: item.image_url,
            description: item.description,
            date: item.date,
            publicationDate: item.publication_date,
            // Les champs suivants ne sont pas fournis par l'API, mais conservés pour compatibilité
            linktableauCommerce: null,
            linktableauService: null,
            titleCommerce: null,
            titleService: null,
            titleMaquette: null,
          }));
          // Trier par date décroissante
          const sorted = mapped.sort((a, b) => new Date(b.date) - new Date(a.date));
          setIcData(sorted);
          setFilteredIC(sorted);
        }
      })
      .catch((err) => {
        setError('Erreur lors du chargement des données.');
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleShowMore = () => {
    setVisibleCount((prevCount) => prevCount + 2);
  };

  const handleYearChange = (event) => {
    const year = event.target.value;
    setSelectedYear(year);
    let filtered;
    if (year === 'Tous') {
      filtered = [...icData];
    } else {
      filtered = icData.filter(
        (indice) => new Date(indice.date).getFullYear() === parseInt(year)
      );
    }
    setFilteredIC(filtered);
    setVisibleCount(4);
  };

  const StyledSelect = styled(Select)(({ theme }) => ({
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.light,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.dark,
    },
  }));

  const theme = createTheme({
    palette: {
      primary: { main: '#388E3C' },
      background: { default: '#f5f5f5' },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h6: {
        fontWeight: '500',
        fontSize: '1rem',
        textTransform: 'none',
        color: '#3B3F54',
      },
      body1: {
        fontSize: 16,
        letterSpacing: 0.5,
        color: '#6B778C',
      },
    },
  });

  const availableYears = useMemo(() => {
    const years = icData.map((indice) => new Date(indice.date).getFullYear());
    return [...new Set(years)].sort((a, b) => b - a);
  }, [icData]);

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <div style={{ backgroundColor: theme.palette.background.default, paddingBottom: '32px' }}>
          <NavigationBar />
          <br /><br />
          <Container maxWidth="lg" style={{ paddingTop: '24px' }}>
            <Typography variant="h4" align="justify" gutterBottom style={{ color: theme.palette.primary.main, marginBottom: '16px' }}>
              Indice du Chiffre d'Affaires au Burundi (ICA)
            </Typography>
            <Divider style={{ margin: '16px 0' }} />
            <Typography variant="body1">Chargement des données...</Typography>
          </Container>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <div style={{ backgroundColor: theme.palette.background.default, paddingBottom: '32px' }}>
          <NavigationBar />
          <br /><br />
          <Container maxWidth="lg" style={{ paddingTop: '24px' }}>
            <Typography variant="h4" align="justify" gutterBottom style={{ color: theme.palette.primary.main, marginBottom: '16px' }}>
              Indice du Chiffre d'Affaires au Burundi (ICA)
            </Typography>
            <Divider style={{ margin: '16px 0' }} />
            <Typography variant="body1" color="error">{error}</Typography>
          </Container>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <div style={{ backgroundColor: theme.palette.background.default, paddingBottom: '32px' }}>
        <NavigationBar />
        <br />
        <br />
        <Container maxWidth="lg" style={{ paddingTop: '24px' }}>
          <Typography variant="h4" align="justify" gutterBottom style={{ color: theme.palette.primary.main, marginBottom: '16px' }}>
            Indice du Chiffre d'Affaires au Burundi (ICA)
          </Typography>
          <Divider style={{ margin: '16px 0' }} />
          <Typography variant="body1" align="justify" paragraph style={{ marginBottom: '24px' }}>
            Le secteur tertiaire constitué des activités commerciales et des services fait appel à l'Indice du Chiffre d'Affaires des services (commerce et autres services) qui est un indicateur permettant de mesurer l'évolution, période par période du chiffre d'affaire réalisé par les entreprises de ce secteur.
          </Typography>

          <FormControl variant="outlined" style={{ marginLeft: '0px', width: '300px' }}>
            <InputLabel id="year-select-label">Filtrer par année de publication</InputLabel>
            <StyledSelect
              labelId="year-select-label"
              id="year-select"
              value={selectedYear}
              onChange={handleYearChange}
              label="Filtrer par année de publication"
            >
              <MenuItem value="Tous">Tous les indices</MenuItem>
              {availableYears.map((year) => (
                <MenuItem key={year} value={year.toString()}>
                  {year}
                </MenuItem>
              ))}
            </StyledSelect>
          </FormControl>

          <Grid container spacing={1} style={{ marginTop: '10px' }}>
            {filteredIC.slice(0, visibleCount).map((indice, index) => (
              <Grid item xs={12} sm={12} key={index}>
                <Card elevation={3} style={{ borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Box sx={{ display: 'flex' }}>
                    <a href={indice.link} style={{ textDecoration: 'none', color: 'inherit' }} target="_blank" rel="noopener noreferrer">
                      <CardMedia
                        component="img"
                        sx={{ width: 101, height: 160, m: 2 }}
                        image={indice.image}
                        alt={indice.title}
                      />
                    </a>
                    <Box sx={{ display: 'flex', flexDirection: 'column', pl: 2, pt: 2 }}>
                      <Typography variant="h6" component="div" style={{ fontWeight: 'bold', marginBottom: '10px', color: theme.palette.primary.main }}>
                        <a href={indice.link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                          <DownloadIcon /> {indice.title}
                        </a>
                      </Typography>
                      <Typography variant="body2" style={{ color: '#555', marginBottom: '10px' }}>
                        {indice.description}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Publié en {moment(indice.date).format('MMMM YYYY')}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box display="flex" justifyContent="space-between" alignItems="center" style={{ marginTop: '20px' }}>
            {visibleCount < filteredIC.length && (
              <Button
                onClick={handleShowMore}
                variant="contained"
                style={{
                  marginTop: '5px',
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                  marginBottom: '10px',
                }}
              >
                Plus d'Indices
              </Button>
            )}
          </Box>

          <Typography variant="body1" align="justify" paragraph style={{ marginBottom: '24px', lineHeight: 1.6 }}>
            <strong>Note :</strong> Les indices du chiffre d'affaires sont publiés trimestriellement, généralement <strong>45 jours</strong> après la fin de chaque trimestre, ce qui correspond aux dates suivantes :
            <ul style={{ listStyleType: 'circle', margin: '10px 0 10px 20px', padding: 0 }}>
              <li><strong>15 février</strong> - Fin du quatrième trimestre</li>
              <li><strong>15 mai</strong> - Fin du premier trimestre</li>
              <li><strong>15 août</strong> - Fin du deuxième trimestre</li>
              <li><strong>15 novembre</strong> - Fin du troisième trimestre</li>
            </ul>
          </Typography>

          <Box display="flex" justifyContent="flex-start" alignItems="center">
            <Button
              onClick={() => navigate('/indice')}
              style={{
                marginTop: '5px',
                backgroundColor: theme.palette.primary.main,
                color: 'white',
              }}
            >
              <ArrowBackIcon style={{ marginRight: '8px' }} />
              Retour à la page des indices
            </Button>
          </Box>
        </Container>
        <br />
        <br />
        <Footer />
      </div>
    </ThemeProvider>
  );
}

export default IndiceChiffreAffaires;